import { Icon } from "@iconify/react";
import vars from "@styles/variables.module.scss";
import {
  Button,
  Checkbox,
  Col,
  ConfigProvider,
  Dropdown,
  Empty,
  Grid,
  Input,
  Menu,
  MenuProps,
  Row,
  Space,
  Table,
  TableColumnsType,
  Typography,
  Upload,
} from "antd";
import { SizeType } from "antd/lib/config-provider/SizeContext";
import { filter, isEmpty, map } from "lodash";
import { ReactNode, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMaskId } from "~/Hooks/useMaskId";
import { useQuery } from "~/Hooks/useQuery";
import { columnWidth } from "./Properties";
import styles from "./index.module.scss";

interface Props {
  tableData: { [key in string]: any }[];
  columns: TableColumnsType;
  tableActions?: {
    items: {
      label: string | ReactNode;
      key: string;
    }[];
    callback: (key: string, record: { [key in string]: string }) => void;
  };
  isLoading?: boolean;
  bulkDelete?: (value: React.Key[]) => void;
  selectedItems?: (value: React.Key[]) => void;
  exportFileName?: string;
  showSelection?: string;
  hideExport?: boolean;
  height?: number;
  onClick?: (record: { [key in string]: any }) => void;
  multiSelect?: boolean;
  showTopBar?: boolean;
  searchQuery?: string;
  clearSearch?: () => void;
  additionalCTA?: ReactNode;
  additionalSearchCols?: any;
  excludeSearchCols?: any;
  noDataText?: string;
  singleDelete?: boolean;
  maxSelection?: number;
  expandable?: boolean;
  expandRenderer?: (values: { [key in string]: any }) => ReactNode;
  className?: string;
  size?: SizeType;
  exportOverrideColumns?: any[];
  exportOverrideDataIndexFields?: any[];
  onSearchChange?: (searchQuery: string, isLoading: boolean) => void;
  onRowExpand?: (expandedKeys: any[]) => void;
  isServerSearchApi?: boolean;
  inputDisable?: boolean;
  isPagination?: boolean;
  totalRecords?: number;
  exportApiUrl?: string;
  recordsPerPage?: number;
  searchPlaceholderText?: string;
  tabOffset?: number;
  hideImport?: boolean;
  onFileUpload?: any;
  exportHandler?: (isExportAll: boolean) => Promise<void>;
  selectedRowKeys?: React.Key[];
  onSelectionChange?: (keys: React.Key[]) => void;
  hideSearch?: boolean;
}

const { useBreakpoint } = Grid;
const costColumns = [
  "applicationSpend",
  "totalCost",
  "adminCost",
  "unitPrice",
  "budgets",
  "projctBudget",
  "projectCost",
  "invoiceAmount",
  "dueAmount",
  "projectBudget",
  "projectAdminCost",
  "savingsAmount",
  "annualSavingsAmount",
  "adminCostYTD",
  "adminApplicationSpend",
  "adminCostYtd",
];

const colRenderColumns = ["userLastLogin"];

const ObjColumns = [
  {
    name: "userApplications",
    value: "applicationName",
  },
  {
    name: "contracts",
    value: "contractName",
  },
  {
    name: "resourceTagsArr",
    value: "value",
  },
];

// should be used as reusable component based on the design
export const DataTable = ({
  tableData,
  columns,
  exportOverrideColumns,
  exportOverrideDataIndexFields,
  tableActions,
  isLoading,
  exportFileName,
  bulkDelete,
  selectedItems,
  totalRecords = 0,
  showSelection,
  hideExport,
  height,
  onClick,
  multiSelect = true,
  showTopBar = true,
  clearSearch,
  searchQuery,
  additionalCTA,
  additionalSearchCols,
  excludeSearchCols,
  noDataText,
  singleDelete,
  maxSelection,
  expandRenderer,
  onRowExpand,
  expandable,
  className,
  size,
  onSearchChange,
  isServerSearchApi,
  inputDisable,
  isPagination = false,
  exportApiUrl,
  recordsPerPage = 0,
  searchPlaceholderText,
  tabOffset = 0,
  hideImport = true,
  onFileUpload,
  exportHandler,
  selectedRowKeys: propSelectedRowKeys,
  onSelectionChange,
  hideSearch = false
}: Props) => {
  const paginationOffset = isPagination ? 60 : 0;
  const [data, setData] = useState<Props["tableData"]>([]);
  const [filteredData, setFilteredData] = useState<Props["tableData"]>([]);
  const [searchString, setSearchString] = useState("");
  // const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [tableHeight, setTableHeight] = useState(window.innerHeight - 260 - paginationOffset - tabOffset);
  const query = useQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeExpRow, setActiveExpRow] = useState<any>([]);
  const [isExportDataLoading, setIsExportDataLoading] = useState<boolean>(false);
  const [currentPageChecked, setCurrentPageChecked] = useState<boolean>(false);
  const [currentPageIndeterminate, setCurrentPageIndeterminate] = useState<boolean>(false);
  const { unmask } = useMaskId();
  const { md } = useBreakpoint();
  const navigate = useNavigate();
  const [file, setFile] = useState("");

  const handleFileChange = (info: any) => {
    setFile(info.file.name);
    onFileUpload(info);
  };

  const activeTab = query.get("activeTab");
  useEffect(() => {
    if (query.get("search")) {
      setSearchString(query.get("search") || searchString);
    }
    return () => setSearchString('')
  }, [query]);

  useEffect(() => {
    if (height) setTableHeight(height);
  }, [height]);

  useEffect(() => {
    setData(tableData);
    setFilteredData(tableData);
  }, [tableData]);

  useEffect(() => {
    if (searchQuery && !isEmpty(searchQuery)) {
      setSearchString(searchQuery);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (!isServerSearchApi) {
      if (!isEmpty(searchString)) {
        let cols = columns.map((col: any) => col.dataIndex);
        if (additionalSearchCols) {
          cols = [...cols, ...additionalSearchCols];
        }
        if (excludeSearchCols && excludeSearchCols?.length > 0) {
          cols = [...cols].filter((col: any) => !excludeSearchCols.includes(col));
        }
        // keys(d)
        let result = filter(
          data,
          (d) =>
            d &&
            cols &&
            cols.some((key) => {
              return String(d[key]).toLowerCase().includes(unmask(searchString).toLowerCase());
            })
        );
        setFilteredData(result);
      } else {
        setFilteredData(data);
      }
    }
  }, [searchString, data]);

  const toggleSelectAll = (e: any) => {
    const isChecked = e.target.checked;
    const currentPageKeys = filteredData.map((r) => r.key);
    let updatedKeys = [...(propSelectedRowKeys || [])];
    if (isChecked) {
      updatedKeys = [...new Set([...updatedKeys, ...currentPageKeys])];
    } else {
      updatedKeys = updatedKeys.filter((key) => !currentPageKeys.includes(key));
    }

    setCurrentPageChecked(isChecked);
    setCurrentPageIndeterminate(false);
    onSelectionChange?.(updatedKeys);
  };

  // Handle individual row selection
  const handleSelect = (record: any, selected: boolean) => {
    let updatedKeys = [...(propSelectedRowKeys || [])];
    if (selected) {
      updatedKeys = [...updatedKeys, record.key];
    } else {
      updatedKeys = updatedKeys.filter((key) => key !== record.key);
    }
    onSelectionChange?.(updatedKeys);
  };

  // Validate checkbox state (checked or indeterminate)
  const validateCheckbox = () => {
    const currentPageKeys = filteredData.map((r) => r.key);
    const selectedKeys = propSelectedRowKeys || [];
    const allSelected = currentPageKeys.length > 0 && currentPageKeys.every((key) => selectedKeys.includes(key));
    const someSelected = currentPageKeys.some((key) => selectedKeys.includes(key));

    setCurrentPageChecked(allSelected);
    setCurrentPageIndeterminate(someSelected && !allSelected);
  };
  useEffect(() => {
    validateCheckbox();
  }, [propSelectedRowKeys, data]);

  useEffect(() => {
    if (data?.length > 0) {
      validateCheckbox();
    } else {
      setCurrentPageChecked(false);
      setCurrentPageIndeterminate(false);
    }
  }, [data]);

  const headerCheckbox = (
    <Checkbox
      checked={currentPageChecked}
      indeterminate={currentPageIndeterminate}
      onChange={toggleSelectAll}
    />
  );

  const rowSelection = {
    selectedRowKeys: propSelectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      if (maxSelection && newSelectedRowKeys.length > maxSelection) {
        return;
      }
      onSelectionChange?.(newSelectedRowKeys);
    },
    onSelect: handleSelect,
    columnTitle: headerCheckbox,
    fixed: true,
  };
  const getActionCol = () => {
    if (tableActions) {
      return [
        {
          title: " ",
          dataIndex: " ",
          width: 30,
          render: (value: any, record: any) => (
            <Dropdown
              overlay={
                <Menu
                  onClick={({ key }) => tableActions && tableActions.callback(key, record)}
                  items={tableActions.items}
                />
              }
              trigger={["click"]}
              placement="bottomLeft"
            >
              <Icon
                icon={"bi:three-dots-vertical"}
                style={{ cursor: "pointer" }}
              />
            </Dropdown>
          ),
          fixed: "right",
        },
      ] as any;
    }
    return [] as any;
  };

  const customizeRenderEmpty = () => (
    <Empty
      style={{ textAlign: "center" }}
      description={noDataText}
      image={Empty.PRESENTED_IMAGE_SIMPLE}
    />
  );

  const handleSearchChange = (value: string) => {
    setSearchString(value);
    onSearchChange && onSearchChange(value, true);
  };

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <div>
          <>
            <Button
              // onClick={async () => {
              //   fetchExportData(true);
              // }}
              onClick={() => exportHandler && exportHandler(true)}
              style={{ width: "100%", textAlign: "left" }}
              type="text"
              disabled={totalRecords === 0}
            >
              All
            </Button>
          </>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div>
          <Button
            style={{ width: "100%", textAlign: "left" }}
            type="text"
            disabled={propSelectedRowKeys?.length === 0}
            // onClick={() => fetchExportData(false)}
            onClick={() => exportHandler && exportHandler(false)}
          >
            Selected
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      {showTopBar && (
        <Row
          gutter={16}
          style={{ marginBottom: vars.whitespace0 }}
        >
          {activeTab === "frequency" ? (
            <>
              {" "}
              <Col span={8}>
                <Input
                  disabled={inputDisable}
                  placeholder={searchPlaceholderText || "Search"}
                  value={searchString}
                  onChange={(e: any) => handleSearchChange(e.target.value)}
                  suffix={
                    searchString ? (
                      <Icon
                        icon="humbleicons:times"
                        height="20"
                        onClick={() => {
                          handleSearchChange("");
                          if (clearSearch) {
                            clearSearch();
                          }
                          if (query.get("search")) {
                            searchParams.delete("search");
                            setSearchParams(searchParams);
                          }
                        }}
                        style={{
                          margin: "auto",
                          color: vars.strawberry,
                          cursor: "pointer",
                        }}
                      />
                    ) : (
                      <Icon
                        icon="eva:search-outline"
                        height="20"
                        style={{ margin: "auto", color: vars.strawberry }}
                      />
                    )
                  }
                />
              </Col>
              <Col
                span={6}
                style={{
                  display: "inline-flex",
                  justifyContent: "end",
                  marginLeft: "auto",
                }}
              >
                <div className={styles.ActionWrapper}>
                  {additionalCTA && additionalCTA}
                  {!hideImport && (
                    <Upload
                      beforeUpload={() => false}
                      onChange={handleFileChange}
                      showUploadList={false}
                      className="ml-4"
                    >
                      <Button
                        type="primary"
                        // loading={isExportDataLoading}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Space>
                            <Icon
                              icon="mdi:table-import"
                              style={{ fontSize: "20px" }}
                              inline
                            />
                            Import
                          </Space>
                        </div>
                      </Button>
                    </Upload>
                  )}
                  {!hideExport && (
                    <Dropdown
                      menu={{ items }}
                      destroyPopupOnHide={true}
                      placement="bottomRight"
                      disabled={isExportDataLoading}
                      // disabled={multiSelect && selectedRowKeys.length <= 0}
                    >
                      <Button
                        type="primary"
                        ghost
                        loading={isExportDataLoading}
                      >
                        <Space>
                          <Icon
                            icon="bi:upload"
                            inline
                          />{" "}
                          Export
                        </Space>
                      </Button>
                    </Dropdown>
                  )}
                </div>
              </Col>
            </>
          ) : (
            <>
              {" "}
              <Col span={8}>
                {!hideSearch && (
                  <Input
                  disabled={inputDisable}
                  placeholder={searchPlaceholderText || "Search"}
                  value={searchString}
                  onChange={(e: any) => handleSearchChange(e.target.value)}
                  suffix={
                    searchString ? (
                      <Icon
                        icon="humbleicons:times"
                        height="20"
                        onClick={() => {
                          handleSearchChange("");
                          if (clearSearch) {
                            clearSearch();
                          }
                          if (query.get("search")) {
                            searchParams.delete("search");
                            setSearchParams(searchParams);
                          }
                        }}
                        style={{
                          margin: "auto",
                          color: vars.strawberry,
                          cursor: "pointer",
                        }}
                      />
                    ) : (
                      <Icon
                        icon="eva:search-outline"
                        height="20"
                        style={{ margin: "auto", color: vars.strawberry }}
                      />
                    )
                  }
                />)}
              </Col>
              <Col
                span={4}
                style={{
                  display: "inline-flex",
                  justifyContent: "end",
                  marginLeft: "auto",
                }}
              >
                <div className={styles.ActionWrapper}>
                  {additionalCTA && additionalCTA}
                  {!hideImport && (
                    <Upload
                      beforeUpload={() => false}
                      onChange={handleFileChange}
                      showUploadList={false}
                      className="ml-4"
                    >
                      <Button
                        type="primary"
                        // loading={isExportDataLoading}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Space>
                            <Icon
                              icon="mdi:table-import"
                              style={{ fontSize: "20px" }}
                              inline
                            />
                            Import
                          </Space>
                        </div>
                      </Button>
                    </Upload>
                  )}
                  {!hideExport && (
                    <Dropdown
                      menu={{ items }}
                      destroyPopupOnHide={true}
                      placement="bottomRight"
                      disabled={isExportDataLoading}
                      // disabled={multiSelect && selectedRowKeys.length <= 0}
                    >
                      <Button
                        type="primary"
                        ghost
                        loading={isExportDataLoading}
                      >
                        <Space>
                          <Icon
                            icon="bi:upload"
                            inline
                          />{" "}
                          Export
                        </Space>
                      </Button>
                    </Dropdown>
                  )}
                </div>
              </Col>
            </>
          )}
        </Row>
      )}
      <Col
        span={12}
        style={{
          display: "inline-flex",
          justifyContent: "end",
          marginLeft: "auto",
        }}
      >
        {showSelection && (
          <Typography.Text ellipsis>
            <span>{`${propSelectedRowKeys?.length} of ${showSelection}`}</span>
          </Typography.Text>
        )}
        {/* <Button>{data.length} {selectedRowKeys?.length}</Button> */}
      </Col>
      <ConfigProvider renderEmpty={noDataText ? customizeRenderEmpty : undefined}>
        <Table
          {...(className && { className })}
          loading={isLoading}
          dataSource={map(filteredData, (item: any) => ({
            ...item,
            key: item.key,
          }))}
          rowClassName={(record, index) => {
            if (index === filteredData?.length - 1) {
              return styles.lastRow;
            }
            return "";
          }}
          columns={[
            ...columns.map((column) => {
              const originalRender = column.render;
              return {
                ...column,
                ...(column.fixed && !md && { fixed: false }),
                ...(column.width ? { width: column.width } : { width: columnWidth.GENERAL }),
                ...(onClick && {
                  onCell: (record: any) => ({ onClick: () => onClick(record) }),
                }),
                showSorterTooltip: false,
                render: (value: any, record: any, index: number) => {
                  if (originalRender) {
                    const renderedValue = originalRender(value, record, index);
                    return renderedValue === undefined || renderedValue === null || renderedValue === ""
                      ? "-"
                      : renderedValue;
                  }
                  return value === undefined || value === null || value === "" ? "-" : value;
                },
              };
            }),
            Table.EXPAND_COLUMN,
            ...getActionCol(),
          ]}
          {...(expandable &&
            expandRenderer && {
              expandable: {
                expandIcon: ({ expanded, onExpand, record }) => (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={(e) => onExpand(record, e)}
                  >
                    <Icon
                      fontSize={20}
                      icon={`${expanded ? "material-symbols:expand-less-rounded" : "material-symbols:expand-more"}`}
                    />
                  </Button>
                ),
                expandedRowRender: expandRenderer,
              },
            })}
          {...(expandable &&
            expandRenderer && {
              expandable: {
                expandedRowKeys: activeExpRow,
                onExpand: (expanded, record) => {
                  const keys = [];
                  if (expanded) {
                    keys.push(record.key);
                  }
                  setActiveExpRow(keys);
                  onRowExpand && onRowExpand(keys);
                },
                expandIcon: ({ expanded, onExpand, record }) =>
                  expanded ? (
                    <Button
                      onClick={(e) => onExpand(record, e)}
                      type="link"
                      style={{ padding: 0 }}
                    >
                      <Icon
                        icon="ic:baseline-keyboard-arrow-up"
                        fontSize={24}
                      />
                    </Button>
                  ) : (
                    <Button
                      onClick={(e) => onExpand(record, e)}
                      type="link"
                      style={{ padding: 0 }}
                    >
                      <Icon
                        icon="ic:outline-keyboard-arrow-down"
                        fontSize={24}
                      />
                    </Button>
                  ),
                expandedRowRender: expandRenderer,
              },
            })}
          {...(size && { size })}
          scroll={{ y: tableHeight - 48 }}
          pagination={false}
          {...(multiSelect && {
            rowSelection: rowSelection,
          })}
        />
      </ConfigProvider>
    </>
  );
};
